import { useState, useEffect } from "react";
import { useAuthStore } from "../store/authStore";
import api from "../services/api";
import { Link } from "react-router-dom";

interface FundSurveyButtonProps {
  surveyId: string;
  title: string;
  description: string;
  budget: string;
  targetResponses: number;
  optionCount: number;
  duration?: number;
  responseWindowHours?: number;
}

// Duration constants for display purposes only
const MIN_DURATION = 3600; // 1 hour
const MAX_DURATION = 30 * 24 * 60 * 60; // 30 days

export function FundSurveyButton({
  surveyId,
  budget,
  targetResponses,
  optionCount,
  duration,
  responseWindowHours,
}: FundSurveyButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fundSuccess, setFundSuccess] = useState(false);
  const { tokens, user } = useAuthStore();

  // Generate survey URL for sharing
  const surveyUrl = `${window.location.origin}/surveys/${surveyId}`;

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(surveyUrl);
      // Could show a toast here
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // Fetch user balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (!tokens?.access_token) return;
      try {
        const response = await api.get("/deposits/balance");
        if (response.data?.success) {
          setUserBalance(response.data.data.balance);
        }
      } catch (err) {
        console.error("Failed to fetch balance:", err);
      }
    };
    fetchBalance();
  }, [tokens?.access_token]);

  // Calculate duration
  const actualDuration = duration
    ? Math.max(MIN_DURATION, Math.min(MAX_DURATION, duration))
    : responseWindowHours
      ? Math.max(MIN_DURATION, Math.min(MAX_DURATION, responseWindowHours * 3600))
      : MIN_DURATION;

  // Format duration for display
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  // Main fund handler - tries points first, falls back to payment options
  const handleFund = () => {
    if (!tokens?.access_token) {
      setError("Please log in to fund this survey");
      return;
    }

    const budgetNum = parseFloat(budget);

    // Check if user has enough points
    if (userBalance !== null && userBalance >= budgetNum) {
      // Show confirmation modal before funding
      setShowConfirmModal(true);
    } else {
      // Not enough balance - show payment options
      setInsufficientBalance(true);
      setShowPaymentOptions(true);
    }
  };

  // Execute the actual funding after confirmation
  const handleConfirmFund = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(`/test/fund-survey`, {
        survey_id: surveyId,
      });

      if (response.data?.success) {
        setFundSuccess(true);
        // Refresh balance after funding
        const balanceResponse = await api.get("/deposits/balance");
        if (balanceResponse.data?.success) {
          setUserBalance(balanceResponse.data.data.balance);
        }
      } else {
        throw new Error(response.data?.error || "Failed to fund survey");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to fund survey. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle funding with Stripe (redirect to checkout)
  const handleFundWithStripe = async () => {
    if (!tokens?.access_token) {
      setError("Please log in to fund this survey");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(`/surveys/${surveyId}/fund-stripe`, {});

      if (response.data?.success && response.data.data?.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.data.checkout_url;
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to initiate Stripe checkout. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const budgetNum = parseFloat(budget);
  const hasEnoughPoints = userBalance !== null && userBalance >= budgetNum;
  const shortfall = budgetNum - (userBalance || 0);

  if (fundSuccess) {
    return (
      <div className="p-6 bg-green-500/10 border border-green-500/20 rounded-lg">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-green-400 mb-2">Survey Funded Successfully!</h3>
          <p className="text-sm text-gray-400 mb-4">
            Your survey is now active and ready to receive responses.
          </p>

          {/* Share Link */}
          <div className="bg-gray-900 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-400 mb-2">Share this link:</p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={surveyUrl}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-300"
              />
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="flex gap-3">
            <Link
              to={`/surveys/${surveyId}`}
              className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors text-sm font-medium"
            >
              View Survey
            </Link>
            <Link
              to="/surveys"
              className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Browse Surveys
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Funding Section */}
      <div className="p-6 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-100">Fund This Survey</h3>
            <p className="text-sm text-gray-400 mt-1">
              {hasEnoughPoints
                ? "You have enough points to fund this survey"
                : "Add points to fund this survey"
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-400">${budget}</p>
            <p className="text-xs text-gray-500">Required</p>
          </div>
        </div>

        {/* User Balance Display */}
        <div className="mb-4 p-3 bg-gray-800/50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-400">Your Balance</span>
            <span className={`text-lg font-bold ${hasEnoughPoints ? 'text-green-400' : 'text-yellow-400'}`}>
              {userBalance !== null ? `${userBalance} points` : 'Loading...'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="bg-gray-800 rounded-md p-3">
            <p className="text-xs text-gray-400">Target Responses</p>
            <p className="text-lg font-bold text-gray-200">{targetResponses}</p>
          </div>
          <div className="bg-gray-800 rounded-md p-3">
            <p className="text-xs text-gray-400">Options</p>
            <p className="text-lg font-bold text-gray-200">{optionCount}</p>
          </div>
          <div className="bg-gray-800 rounded-md p-3">
            <p className="text-xs text-gray-400">Duration</p>
            <p className="text-lg font-bold text-gray-200">{formatDuration(actualDuration)}</p>
          </div>
        </div>

        {/* Main Action */}
        {!showPaymentOptions ? (
          <button
            onClick={handleFund}
            disabled={isLoading}
            className={`w-full py-3 px-4 font-semibold rounded-lg transition-all ${
              isLoading
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : hasEnoughPoints
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-yellow-600 hover:bg-yellow-700 text-white"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </span>
            ) : hasEnoughPoints ? (
              `Fund with Points (${userBalance} available)`
            ) : (
              `Add ${shortfall} Points to Fund`
            )}
          </button>
        ) : (
          <div className="space-y-3">
            {insufficientBalance && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg text-center">
                <p className="text-sm text-yellow-400">
                  Insufficient balance. You need <strong>{budgetNum} points</strong> but have <strong>{userBalance} points</strong>.
                </p>
              </div>
            )}

            <p className="text-sm text-gray-400 text-center">Choose how to add points:</p>

            {/* Option 1: Deposit Points (Stripe) */}
            <button
              onClick={handleFundWithStripe}
              disabled={isLoading}
              className="w-full py-3 px-4 font-semibold rounded-lg transition-all bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
            >
              {isLoading ? "Processing..." : `Pay $${budget} with Card (Stripe)`}
            </button>

            {/* Option 2: Go to Deposit Page */}
            <Link
              to="/deposit"
              className="block w-full py-3 px-4 font-semibold rounded-lg transition-all bg-gray-700 hover:bg-gray-600 text-white text-center"
            >
              Go to Deposit Page
            </Link>

            <button
              onClick={() => {
                setShowPaymentOptions(false);
                setInsufficientBalance(false);
              }}
              disabled={isLoading}
              className="w-full py-2 px-4 text-sm text-gray-400 hover:text-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        <div className="mt-4 text-xs text-gray-500">
          <p>
            {hasEnoughPoints
              ? "💡 Click to fund instantly using your points balance"
              : "💡 You'll be redirected to add points before funding"
            }
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-xl font-semibold text-gray-100 mb-2">Confirm Funding</h3>
            <p className="text-gray-400 mb-4">
              Are you sure you want to fund this survey with <strong className="text-green-400">{budget} points</strong>?
            </p>

            <div className="bg-gray-900 rounded-lg p-4 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Survey Budget:</span>
                <span className="text-gray-300">{budget} points</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Your Balance:</span>
                <span className="text-gray-300">{userBalance} points</span>
              </div>
              <div className="border-t border-gray-700 pt-2 flex justify-between text-sm">
                <span className="text-gray-500">Remaining After:</span>
                <span className="text-green-400">{(userBalance || 0) - budgetNum} points</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isLoading}
                className="flex-1 py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFund}
                disabled={isLoading}
                className="flex-1 py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "Confirm Fund"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
